export interface ExperienceTicketParticipant {
  travellerType?: 'adult' | 'child'
  /** Index of the purchased unit this participant belongs to (e.g. cabana/table number) */
  unitIndex: number
}

export interface ExperienceTicketParticipantCounts {
  adults: number
  children: number
}

type ExperienceParticipantTicket = Pick<
  App.Checkout.ExperienceItemTicketSummaryView,
  'count' | 'participantsPerUnit' | 'numberOfAdults' | 'numberOfChildren'
>

function getUnitIndex(index: number, participantsPerUnit = 1): number {
  return Math.floor(index / participantsPerUnit)
}

/**
 * Expands an experience ticket into the individual participants svc-traveller
 * generates traveller forms for.
 *
 * This is the single source of truth shared by the traveller form schema request
 * (getExperiencesSchemaRequestItems) and the insurance quote occupancy
 * (getExperienceParticipantOccupants) so the two sides cannot drift — when they
 * disagree, the order submits a different traveller count than the insurance
 * quote was fetched for and the provider rejects the booking after payment.
 *
 * Rules:
 * - Tickets with an adult/child split expand to numberOfAdults/numberOfChildren
 *   participants per purchased unit, typed accordingly.
 * - Any other ticket expands to participantsPerUnit participants per purchased
 *   unit (defaulting to 1), untyped — svc-traveller keys untyped participant
 *   forms as adults.
 */
export function expandExperienceTicketParticipants(ticket: ExperienceParticipantTicket): Array<ExperienceTicketParticipant> {
  const hasAdultChildSplit = (ticket.numberOfAdults ?? 0) > 0 || (ticket.numberOfChildren ?? 0) > 0

  if (hasAdultChildSplit) {
    const adultCount = (ticket.numberOfAdults ?? 0) * ticket.count
    const childCount = (ticket.numberOfChildren ?? 0) * ticket.count

    return [
      ...Array.from({ length: adultCount }, (_, index): ExperienceTicketParticipant => ({
        travellerType: 'adult',
        unitIndex: getUnitIndex(index, ticket.numberOfAdults),
      })),
      ...Array.from({ length: childCount }, (_, index): ExperienceTicketParticipant => ({
        travellerType: 'child',
        unitIndex: getUnitIndex(index, ticket.numberOfChildren),
      })),
    ]
  }

  const participantsPerUnit = ticket.participantsPerUnit && ticket.participantsPerUnit > 0 ? ticket.participantsPerUnit : 1
  return Array.from({ length: ticket.count * participantsPerUnit }, (_, index): ExperienceTicketParticipant => ({
    unitIndex: getUnitIndex(index, participantsPerUnit),
  }))
}

/**
 * Counts the participants of an item's experience tickets per age group,
 * matching the traveller forms svc-traveller generates for that item
 * (untyped participants become adult forms).
 */
export function countExperienceTicketParticipants(tickets: Array<ExperienceParticipantTicket>): ExperienceTicketParticipantCounts {
  const participants = tickets.flatMap(expandExperienceTicketParticipants)
  return {
    adults: participants.filter(participant => participant.travellerType !== 'child').length,
    children: participants.filter(participant => participant.travellerType === 'child').length,
  }
}
